// src/components/manager/booking/ServiceManager.jsx
import React, { useState, useEffect } from 'react';
import { 
  Plus, Edit, Trash2, Clock, DollarSign, Users, AlertTriangle,
  Calendar, Settings, Copy, Eye, X, Save
} from 'lucide-react';
import { SectionBuilder } from '../../shared/forms/SectionBuilder';
import { getBookingFieldConfig } from '../../../services/bookingFieldConfiguration';
import UniversalField from '../../shared/forms/UniversalField';
import { FIELD_TYPES } from '../../shared/forms/FieldTypes';

export const ServiceManager = ({ bookId, onUpdate }) => {
  const [services, setServices] = useState([]);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [loading, setLoading] = useState(true);

  const serviceTypeConfigs = {
    appointment: {
      icon: '📋',
      title: 'Appointments',
      description: 'One-on-one meetings with staff members',
      color: 'bg-blue-100 text-blue-700'
    },
    consultation: {
      icon: '💡',
      title: 'Consultations',
      description: 'Strategic planning and advisory sessions',
      color: 'bg-purple-100 text-purple-700'
    },
    class: {
      icon: '🎓',
      title: 'Classes & Workshops',
      description: 'Group learning sessions and training',
      color: 'bg-green-100 text-green-700'
    },
    facility: {
      icon: '🏢',
      title: 'Facility Bookings',
      description: 'Room and space reservations',
      color: 'bg-orange-100 text-orange-700'
    }
  };

  useEffect(() => {
    loadServices();
  }, [bookId]);

  const loadServices = async () => {
    try {
      setLoading(true);
      // Load services from your booking service
      const servicesData = []; // await bookingService.getServices(bookId);
      setServices(servicesData);
    } catch (error) {
      console.error('Error loading services:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateService = (serviceType) => {
    setEditingService({
      type: serviceType,
      name: '',
      description: '',
      duration: 60,
      price: 0,
      capacity: 1,
      staff: [],
      availability: {},
      customFields: []
    });
    setShowCreateForm(true);
  };

  const handleSaveService = async (serviceData) => {
    try {
      if (editingService?.id) {
        // Update existing service
        // await bookingService.updateService(bookId, editingService.id, serviceData);
      } else {
        // Create new service
        // await bookingService.createService(bookId, serviceData);
      }
      
      await loadServices();
      setShowCreateForm(false);
      setEditingService(null);
    } catch (error) {
      console.error('Error saving service:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin h-8 w-8 border-2 border-blue-600 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto space-y-8">
      {/* Header */}
      <div className="text-center">
        <h2 className="text-3xl font-bold text-gray-900 mb-2">Booking Services</h2>
        <p className="text-gray-600">Manage the services and appointments your organization offers</p>
      </div>

      {/* Service Type Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {Object.entries(serviceTypeConfigs).map(([type, config]) => (
          <div key={type} className="bg-white rounded-lg border border-gray-200 p-6 hover:shadow-lg transition-shadow">
            <div className={`w-12 h-12 rounded-lg ${config.color} flex items-center justify-center text-2xl mb-4`}>
              {config.icon}
            </div>
            <h3 className="font-semibold text-gray-900 mb-2">{config.title}</h3>
            <p className="text-sm text-gray-600 mb-4">{config.description}</p>
            
            <button
              onClick={() => handleCreateService(type)}
              className="w-full bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
            >
              <Plus className="h-4 w-4" />
              Create {config.title.split(' ')[0]}
            </button>
          </div>
        ))}
      </div>

      {/* Existing Services */}
      {services.length > 0 && (
        <div className="space-y-6">
          <h3 className="text-xl font-semibold text-gray-900">Your Services</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map(service => (
              <ServiceCard
                key={service.id}
                service={service}
                config={serviceTypeConfigs[service.type]}
                onEdit={() => {
                  setEditingService(service);
                  setShowCreateForm(true);
                }}
                onDelete={(id) => {
                  if (window.confirm('Delete this service?')) {
                    // Handle delete
                  }
                }}
              />
            ))}
          </div>
        </div>
      )}

      {/* Service Form Modal */}
      {showCreateForm && (
        <ServiceFormModal
          service={editingService}
          isOpen={showCreateForm}
          onClose={() => {
            setShowCreateForm(false);
            setEditingService(null);
          }}
          onSave={handleSaveService}
        />
      )}
    </div>
  );
};

// Service Card Component
const ServiceCard = ({ service, config, onEdit, onDelete }) => {
  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6">
      <div className="flex items-start justify-between mb-4">
        <div className={`w-10 h-10 rounded-lg ${config.color} flex items-center justify-center text-lg`}>
          {config.icon}
        </div>
        <div className="flex gap-2">
          <button onClick={onEdit} className="p-1 text-gray-500 hover:text-blue-600">
            <Edit className="h-4 w-4" />
          </button>
          <button onClick={() => onDelete(service.id)} className="p-1 text-gray-500 hover:text-red-600">
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      </div>
      
      <h4 className="font-semibold text-gray-900 mb-2">{service.name}</h4>
      <p className="text-sm text-gray-600 mb-4">{service.description}</p>
      
      <div className="space-y-2 text-sm text-gray-600">
        <div className="flex items-center gap-2">
          <Clock className="h-4 w-4" />
          {service.duration} minutes
        </div>
        <div className="flex items-center gap-2">
          <DollarSign className="h-4 w-4" />
          {service.price > 0 ? `R${service.price}` : 'Free'}
        </div>
        <div className="flex items-center gap-2">
          <Users className="h-4 w-4" />
          Up to {service.capacity} people
        </div>
      </div>
      
      <div className="mt-4 pt-4 border-t border-gray-100">
        <button className="text-blue-600 hover:text-blue-700 text-sm font-medium">
          View Bookings
        </button>
      </div>
    </div>
  );
};

// Service Form Modal Component
const ServiceFormModal = ({ service, isOpen, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    type: 'appointment',
    duration: 60,
    price: 0,
    capacity: 1,
    requireApproval: false,
    allowRescheduling: true,
    bufferTime: 15,
    maxAdvanceBooking: 30,
    ...service
  });

  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  const serviceFields = [
    {
      name: 'name',
      type: FIELD_TYPES.TEXT,
      label: 'Service Name',
      placeholder: 'e.g., Initial Consultation',
      required: true
    },
    {
      name: 'description',
      type: FIELD_TYPES.TEXTAREA,
      label: 'Description',
      placeholder: 'Describe what this service includes...',
      rows: 3
    },
    {
      name: 'type',
      type: FIELD_TYPES.SELECT,
      label: 'Service Type',
      options: [
        { value: 'appointment', label: '📋 Appointment' },
        { value: 'consultation', label: '💡 Consultation' },
        { value: 'class', label: '🎓 Class/Workshop' },
        { value: 'facility', label: '🏢 Facility Booking' }
      ],
      required: true
    },
    {
      name: 'duration',
      type: FIELD_TYPES.SELECT,
      label: 'Duration (minutes)',
      options: [
        { value: 15, label: '15 minutes' },
        { value: 30, label: '30 minutes' },
        { value: 45, label: '45 minutes' },
        { value: 60, label: '1 hour' },
        { value: 90, label: '1.5 hours' },
        { value: 120, label: '2 hours' },
        { value: 180, label: '3 hours' }
      ],
      required: true
    },
    {
      name: 'price',
      type: FIELD_TYPES.CURRENCY,
      label: 'Price',
      placeholder: '0.00'
    },
    {
      name: 'capacity',
      type: FIELD_TYPES.NUMBER,
      label: 'Maximum Capacity',
      placeholder: '1',
      min: 1,
      max: 100
    }
  ];

  const advancedFields = [
    {
      name: 'requireApproval',
      type: FIELD_TYPES.TOGGLE,
      label: 'Require Manual Approval',
      description: 'Bookings need staff approval before confirmation'
    },
    {
      name: 'allowRescheduling',
      type: FIELD_TYPES.TOGGLE,
      label: 'Allow Customer Rescheduling',
      description: 'Customers can reschedule their own bookings'
    },
    {
      name: 'bufferTime',
      type: FIELD_TYPES.SELECT,
      label: 'Buffer Time Between Bookings',
      options: [
        { value: 0, label: 'No buffer' },
        { value: 5, label: '5 minutes' },
        { value: 15, label: '15 minutes' },
        { value: 30, label: '30 minutes' }
      ]
    },
    {
      name: 'maxAdvanceBooking',
      type: FIELD_TYPES.SELECT,
      label: 'Maximum Advance Booking',
      options: [
        { value: 7, label: '1 week' },
        { value: 14, label: '2 weeks' },
        { value: 30, label: '1 month' },
        { value: 60, label: '2 months' },
        { value: 90, label: '3 months' }
      ]
    }
  ];

  const handleFieldChange = (fieldName, value) => {
    setFormData(prev => ({ ...prev, [fieldName]: value }));
    // Clear error for this field
    if (errors[fieldName]) {
      setErrors(prev => ({ ...prev, [fieldName]: null }));
    }
  };

  const handleSave = async () => {
    // Validate required fields
    const newErrors = {};
    if (!formData.name?.trim()) newErrors.name = 'Service name is required';
    if (!formData.type) newErrors.type = 'Service type is required';
    if (!formData.duration) newErrors.duration = 'Duration is required';

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    try {
      setSaving(true);
      await onSave(formData);
    } catch (error) {
      console.error('Error saving service:', error);
      setErrors({ general: 'Failed to save service. Please try again.' });
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">
            {service?.id ? 'Edit Service' : 'Create New Service'}
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        {/* Form Content */}
        <div className="overflow-y-auto max-h-[calc(90vh-140px)]">
          <div className="p-6 space-y-6">
            {/* General Error */}
            {errors.general && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex items-center gap-2 text-red-800">
                  <AlertTriangle className="h-5 w-5" />
                  {errors.general}
                </div>
              </div>
            )}

            {/* Basic Information */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Basic Information</h3>
              <div className="space-y-4">
                {serviceFields.map(field => (
                  <UniversalField
                    key={field.name}
                    field={field}
                    value={formData[field.name]}
                    onChange={(value) => handleFieldChange(field.name, value)}
                    error={errors[field.name]}
                  />
                ))}
              </div>
            </div>

            {/* Advanced Settings */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Advanced Settings</h3>
              <div className="space-y-4">
                {advancedFields.map(field => (
                  <UniversalField
                    key={field.name}
                    field={field}
                    value={formData[field.name]}
                    onChange={(value) => handleFieldChange(field.name, value)}
                    error={errors[field.name]}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-4 p-6 border-t border-gray-200">
          <button
            onClick={onClose}
            disabled={saving}
            className="px-4 py-2 text-gray-600 hover:text-gray-800 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? (
              <>
                <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                Saving...
              </>
            ) : (
              <>
                <Save className="h-4 w-4" />
                {service?.id ? 'Update Service' : 'Create Service'}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};